import React, { useEffect } from 'react'
import { connect, ConnectedProps, useSelector } from 'react-redux'
import { DsCarousel, DsStack, DsTypography } from '@am92/react-design-system'

import getWithPageConstants, {
  IWithPageConstantsProps
} from '~/src/Hocs/withPageConstants'

import SubscriptionCard from './Components/SubscriptionCard'
import { DynamicString } from '~/src/Components/DynamicString'
import Loader from '~/src/Components/Loader'
import PageWrapper from '~/src/Components/PageWrapper'

import PRICING_PAGE_CONSTANTS from './Pricing.PageConstants'
import { T_PRICING_PAGE_CONSTANTS } from './Pricing.PageConstants/TYPES'

import { isServiceLoading } from '~/src/Redux/ServiceTracker/Selectors'
import { fetchSubscriptionsServiceName } from '~/src/Redux/Subscription/Actions'
import { getSubscriptionListSubscriptionSelector } from '~/src/Redux/Subscription/Selectors'
import fetchSubscriptionsService from '~/src/Redux/Subscription/Services/fetchSubscriptions.Service'

import { TAppDispatch, TAppStore } from '~/src/Configurations/AppStore'

export interface IPricingPageProps
  extends IWithPageConstantsProps<T_PRICING_PAGE_CONSTANTS>,
    PropsFromRedux {}

const PricingPage = (props: IPricingPageProps) => {
  const { PAGE_CONSTANTS, subscriptions } = props
  const subscriptionLength = subscriptions.length
  const isLoading = useSelector((state: TAppStore) =>
    isServiceLoading(state, [fetchSubscriptionsServiceName])
  )

  useEffect(() => {
    const { actions } = props
    actions.fetchSubscriptionsService()
  }, [])

  return (
    <>
      {isLoading && <Loader />}
      <PageWrapper>
        <DsStack
          spacing='var(--ds-spacing-frostbite)'
          justifyContent='center'
          alignItems='center'
        >
          <DsTypography variant='headingBoldExtraLarge' textAlign='center'>
            {PAGE_CONSTANTS.TITLE}
          </DsTypography>
          <DynamicString
            dynamicString={PAGE_CONSTANTS.DESCRIPTION}
            variant='bodyRegularLarge'
            textAlign='center'
          />
        </DsStack>
        <DsCarousel
          autoplay={false}
          autoHeight={false}
          pagination={{
            enabled: true,
            mode: 'external'
          }}
          SwiperConatinerWrapperProps={{
            sx: {
              maxWidth: '960px',
              margin: '0 auto',
              px: {
                xs: 'var(--ds-spacing-tropical)',
                md: 'var(--ds-spacing-molten)'
              }
            }
          }}
          NavigationProps={{
            PrevButtonProps: {
              IconProps: {
                className: 'ri-arrow-left-line',
                sx: {
                  fontSize: {
                    xs: 'var(--ds-spacing-mild)',
                    md: 'var(--ds-spacing-mild)'
                  }
                }
              },
              sx: {
                display: {
                  xs: subscriptionLength > 1 ? 'flex' : 'none',
                  sm: subscriptionLength < 1 ? 'none' : 'flex',
                  md: subscriptionLength < 2 ? 'none' : 'flex',
                  lg: subscriptionLength < 4 ? 'none' : 'flex',
                  xl: subscriptionLength < 4 ? 'none' : 'flex'
                },
                height: { xs: '40px', md: '56px' },
                width: { xs: '40px', md: '56px' },
                border: '1px solid var(--ds-colour-strokeDefault)',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2) !important',
                backdropFilter: 'blur(24px)',
                left: '0px',
                '&.Mui-disabled': {
                  pointerEvents: 'unset'
                }
              }
            },
            NextButtonProps: {
              IconProps: {
                className: 'ri-arrow-right-line',
                sx: {
                  fontSize: {
                    xs: 'var(--ds-spacing-mild)',
                    md: 'var(--ds-spacing-mild)'
                  }
                }
              },
              sx: {
                display: {
                  xs: subscriptionLength > 1 ? 'flex' : 'none',
                  sm: subscriptionLength < 1 ? 'none' : 'flex',
                  md: subscriptionLength < 2 ? 'none' : 'flex',
                  lg: subscriptionLength < 4 ? 'none' : 'flex',
                  xl: subscriptionLength < 4 ? 'none' : 'flex'
                },
                height: { xs: '40px', md: '56px' },
                width: { xs: '40px', md: '56px' },
                border: '1px solid var(--ds-colour-strokeDefault)',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2) !important',
                backdropFilter: 'blur(24px)',
                right: '0px',
                '&.Mui-disabled': {
                  pointerEvents: 'unset'
                }
              }
            }
          }}
          breakpoints={{
            0: {
              slidesPerView: 1,
              spaceBetween: 16
            },
            414: {
              slidesPerView: 1,
              spaceBetween: 16
            },
            744: {
              slidesPerView: 2,
              spaceBetween: 12
            },
            1280: {
              slidesPerView: 3,
              spaceBetween: 12
            },
            1440: {
              slidesPerView: 3,
              spaceBetween: 12
            }
          }}
        >
          {subscriptions.map(subscription => (
            <SubscriptionCard
              subscription={subscription}
              key={subscription.planName}
            />
          ))}
        </DsCarousel>
      </PageWrapper>
    </>
  )
}

const mapStateToProps = (state: TAppStore) => {
  const isLoading = isServiceLoading(state, [fetchSubscriptionsServiceName])
  const subscriptions = getSubscriptionListSubscriptionSelector(state)
  return { isLoading, subscriptions }
}

const mapDispatchToProps = (dispatch: TAppDispatch) => ({
  actions: {
    fetchSubscriptionsService: () => dispatch(fetchSubscriptionsService())
  }
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

const withPageConstants = getWithPageConstants<T_PRICING_PAGE_CONSTANTS>(
  PRICING_PAGE_CONSTANTS
)

export default withPageConstants(connector(PricingPage))
